import { useEffect, useMemo, useRef } from 'react'

import logger from '@/utils/logger'

const IPC_TIMEOUT_MS = 2000

type API =
  | {
      type: 'push-subscribe'
      response: undefined
    }
  | {
      type: 'push-permission-token'
      response: undefined
    }
  | {
      type: 'push-permission-request'
      response: 'denied' | 'granted'
    }
  | {
      type: 'push-permission-state'
      response: 'notDetermined' | 'denied' | 'authorized' | 'ephemeral' | 'provisional' | 'unknown'
    }
  | {
      type: 'push-permission-open-settings'
      response: undefined
    }

// TODO: "push-notification" event
// TODO: "push-subscribe" action
export default function useIosIpc() {
  const queue = useRef(
    new Set<{
      type: string
      callback: (response: unknown) => void
    }>()
  )

  useEffect(() => {
    const listener = (event: Event) => {
      try {
        const payload = (event as Event & { detail: API }).detail
        queue.current.forEach(item => {
          if (item.type !== payload.type) {
            return
          }
          queue.current.delete(item)
          try {
            item.callback(payload.response)
          } catch (err) {
            logger.error('Error in iOS IPC Queue', { err })
          }
        })
      } catch (err) {
        logger.error('Error in iOS IPC listener', { err })
      }
    }

    globalThis.addEventListener('ios-ipc', listener)

    return () => {
      globalThis.removeEventListener('ios-ipc', listener)
    }
  }, [])

  const api = useMemo(() => {
    function request<T extends API['type']>(
      type: T,
      args: string[],
      skipQueue = false
    ): Promise<Extract<API, { type: T }>['response']> {
      const promise = new Promise<unknown>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Request timed out'))
        }, IPC_TIMEOUT_MS)

        const queueItem = {
          type,
          callback(data: unknown) {
            clearTimeout(timeout)
            resolve(data)
          },
        }
        function failureCallback(err: Error) {
          clearTimeout(timeout)
          if (!skipQueue) {
            queue.current.delete(queueItem)
          }
          reject(err)
        }

        if (!skipQueue) {
          queue.current.add(queueItem)
        }

        try {
          // @ts-expect-error: Custom WKWebKit prop
          globalThis.webkit.messageHandlers[type]
            .postMessage(args)
            .then(skipQueue ? resolve : null, failureCallback)
        } catch (err) {
          failureCallback(err)
        }
      })

      // @ts-expect-error: Complex type. YOLO
      return promise as Promise<Extract<API, { type: T }>['response']>
    }

    return {
      // TODO: We need a payload for this?
      pushSubscribe: () => request('push-subscribe', []),
      pushPermissionToken: () => request('push-permission-token', []),
      pushPermissionRequest: () => request('push-permission-request', []),
      pushPermissionState: () => request('push-permission-state', []),
      pushPermissionOpenSettings: () => request('push-permission-open-settings', [], true),
    }
  }, [])

  return api
}
